import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { KeyRound, LogOut } from "lucide-react"
import { useAuth } from "../../hooks/useAuth"
import { getRoutines } from "../../api/routines"
import { getBodyMetrics, createBodyMetric } from "../../api/bodyMetrics"
import { getWorkoutLogs, createWorkoutLog, deleteWorkoutLog } from "../../api/workoutLogs"
import { getMemberships } from "../../api/memberships"
import type { Routine, BodyMetric, WorkoutLog, Membership } from "../../types"
import WorkoutSession from "./WorkoutSession"

const MUSCLE_COLORS: Record<string, string> = {
  legs: "bg-green-500/20 text-green-400",
  chest: "bg-red-500/20 text-red-400",
  back: "bg-blue-500/20 text-blue-400",
  shoulders: "bg-purple-500/20 text-purple-400",
  arms: "bg-orange-500/20 text-orange-400",
  core: "bg-yellow-500/20 text-yellow-400",
}
function getMuscleColor(group: string) {
  return MUSCLE_COLORS[group?.toLowerCase()] ?? "bg-white/10 text-white/60"
}

function toISODate(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export default function MemberDashboard() {
  const { user, logout } = useAuth()
  const today = toISODate(new Date())

  const [todayRoutines, setTodayRoutines] = useState<Routine[]>([])
  const [allRoutines, setAllRoutines] = useState<Routine[]>([])
  const [metrics, setMetrics] = useState<BodyMetric[]>([])
  const [logs, setLogs] = useState<WorkoutLog[]>([])
  const [membership, setMembership] = useState<Membership | null>(null)
  const [loading, setLoading] = useState(true)

  // Quick log state
  const [logRoutineId, setLogRoutineId] = useState<number | null>(null)
  const [logDuration, setLogDuration] = useState("")
  const [loggingSaving, setLoggingSaving] = useState(false)

  // Workout session
  const [activeSession, setActiveSession] = useState<Routine | null>(null)

  // Quick metric state
  const [showMetricForm, setShowMetricForm] = useState(false)
  const [mWeight, setMWeight] = useState("")
  const [mFat, setMFat] = useState("")
  const [mMuscle, setMMuscle] = useState("")
  const [metricSaving, setMetricSaving] = useState(false)

  useEffect(() => {
    Promise.all([
      getRoutines({ date: today }),
      getRoutines(),
      getBodyMetrics(),
      getWorkoutLogs(),
      getMemberships(),
    ])
      .then(([todayRts, allRts, bm, wl, memberships]) => {
        setTodayRoutines(todayRts)
        setAllRoutines(allRts)
        setMetrics(bm)
        setLogs(wl)
        setMembership(memberships[0] ?? null)
      })
      .finally(() => setLoading(false))
  }, [today])

  async function handleUndoComplete(routineId: number) {
    const log = logs.find((l) => l.routine_id === routineId && l.performed_at.startsWith(today))
    if (!log) return
    await deleteWorkoutLog(log.id)
    setLogs((prev) => prev.filter((l) => l.id !== log.id))
  }

  async function handleLogWorkout() {
    if (!logRoutineId) return
    setLoggingSaving(true)
    try {
      const log = await createWorkoutLog({
        routine_id: logRoutineId,
        performed_at: today,
        duration_minutes: logDuration ? Number(logDuration) : undefined,
      })
      setLogs((prev) => [log, ...prev])
      setLogRoutineId(null)
      setLogDuration("")
    } finally {
      setLoggingSaving(false)
    }
  }

  async function handleAddMetric() {
    setMetricSaving(true)
    try {
      const m = await createBodyMetric({
        recorded_at: today,
        weight: mWeight ? Number(mWeight) : undefined,
        body_fat: mFat ? Number(mFat) : undefined,
        muscle_mass: mMuscle ? Number(mMuscle) : undefined,
      })
      setMetrics((prev) => [m, ...prev])
      setMWeight("")
      setMFat("")
      setMMuscle("")
      setShowMetricForm(false)
    } finally {
      setMetricSaving(false)
    }
  }

  const latestMetric = metrics[0]

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  if (membership?.status === "inactive") {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <div className="w-20 h-20 rounded-full bg-danger/10 border border-danger/30 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-danger">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <div className="space-y-2 max-w-sm">
          <h2 className="text-xl font-semibold text-white">Suscripción finalizada</h2>
          <p className="text-sm text-muted leading-relaxed">
            Su suscripción ha finalizado. Si desea continuar con otro programa, póngase en contacto con el coach.
          </p>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 rounded-lg border border-white/10 px-5 py-2.5 text-sm text-muted hover:text-white transition"
        >
          <LogOut size={15} />
          Cerrar sesión
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {activeSession && (
        <WorkoutSession
          routine={activeSession}
          today={today}
          onComplete={() => {
            getWorkoutLogs().then(setLogs)
            setActiveSession(null)
          }}
          onClose={() => setActiveSession(null)}
        />
      )}
      {/* Header */}
      <header className="border-b border-white/10 bg-surface px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg sm:text-xl font-bold tracking-wider text-white">SCIENCE FITNESS</h1>
            <p className="text-xs sm:text-sm text-muted">Welcome back, {user?.name}</p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/change-password"
              className="text-sm text-muted hover:text-primary transition"
              title="Cambiar contraseña"
            >
              <span className="hidden sm:inline">Cambiar contraseña</span>
              <span className="sm:hidden">
                <KeyRound size={16} />
              </span>
            </Link>
            <button
              onClick={() => logout()}
              className="text-sm text-muted hover:text-white transition"
            >
              <span className="hidden sm:inline">Sign Out</span>
              <span className="sm:hidden">
                <LogOut size={16} />
              </span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl p-4 sm:p-6 space-y-6">
        {/* Today's Training */}
        <section>
          <h2 className="mb-3 text-lg font-semibold text-white">
            Today&apos;s Training
            <span className="ml-2 text-sm font-normal text-muted">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </span>
          </h2>

          {todayRoutines.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-surface p-6 text-center text-muted">
              <p>No training scheduled for today.</p>
              <p className="mt-1 text-xs">Your trainer will assign routines for upcoming days.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {todayRoutines.map((routine) => (
                <div key={routine.id} className="rounded-xl border border-white/10 bg-surface overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-surface-light">
                    <div>
                      <p className="font-medium text-white">{routine.name}</p>
                      <p className="text-xs text-muted">
                        {routine.routine_exercises.length} exercises
                      </p>
                    </div>
                    {logs.some((l) => l.routine_id === routine.id && l.performed_at.startsWith(today)) ? (
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-success/20 px-2.5 py-1 text-xs text-success">
                          Completado
                        </span>
                        <button
                          onClick={() => handleUndoComplete(routine.id)}
                          className="rounded-full border border-white/15 px-2.5 py-1 text-xs text-muted hover:text-white hover:border-white/35 transition"
                        >
                          Deshacer
                        </button>
                      </div>
                    ) : logRoutineId === routine.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={logDuration}
                          onChange={(e) => setLogDuration(e.target.value)}
                          placeholder="min"
                          className="w-16 rounded-lg border border-white/10 bg-surface px-2 py-1 text-xs text-white outline-none focus:border-primary"
                        />
                        <button
                          onClick={handleLogWorkout}
                          disabled={loggingSaving}
                          className="rounded-lg bg-success/20 px-3 py-1 text-xs text-success hover:bg-success/30 transition"
                        >
                          {loggingSaving ? "..." : "Listo"}
                        </button>
                        <button
                          onClick={() => setLogRoutineId(null)}
                          className="text-xs text-muted hover:text-white"
                        >
                          &times;
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setActiveSession(routine)}
                          className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-black hover:bg-primary-hover transition"
                        >
                          Empezar
                        </button>
                        <button
                          onClick={() => setLogRoutineId(routine.id)}
                          className="rounded-full border border-white/20 px-3 py-1.5 text-xs text-muted hover:text-white hover:border-white/40 transition"
                        >
                          Completar
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="px-4 py-3 space-y-2">
                    {routine.routine_exercises.map((re) => (
                      <div key={re.id} className="flex items-center gap-3">
                        <span
                          className={`rounded-full px-1.5 py-0.5 text-xs ${getMuscleColor(re.exercise.muscle_group)}`}
                        >
                          {re.exercise.muscle_group}
                        </span>
                        <span className="text-sm text-white flex-1">{re.exercise.name}</span>
                        <span className="text-xs text-muted">
                          {re.sets}×{re.reps}
                          {re.weight ? ` · ${re.weight} ${re.weight_unit}` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Body Metrics + Workout History */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Metrics */}
          <section className="rounded-xl border border-white/10 bg-surface">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">Body Metrics</h3>
              <button
                onClick={() => setShowMetricForm((v) => !v)}
                className="text-xs text-primary hover:text-primary-hover transition"
              >
                + Record
              </button>
            </div>

            {latestMetric && (
              <div className="grid grid-cols-3 gap-3 px-4 py-3 border-b border-white/5">
                {[
                  { label: "Weight", value: latestMetric.weight ? `${latestMetric.weight} kg` : "—" },
                  { label: "Body Fat", value: latestMetric.body_fat ? `${latestMetric.body_fat}%` : "—" },
                  { label: "Muscle", value: latestMetric.muscle_mass ? `${latestMetric.muscle_mass} kg` : "—" },
                ].map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="text-xs text-muted">{s.label}</p>
                    <p className="text-lg font-bold text-primary">{s.value}</p>
                  </div>
                ))}
              </div>
            )}

            {showMetricForm && (
              <div className="px-4 py-3 border-b border-white/5 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    value={mWeight}
                    onChange={(e) => setMWeight(e.target.value)}
                    placeholder="Weight"
                    className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
                  />
                  <input
                    type="number"
                    value={mFat}
                    onChange={(e) => setMFat(e.target.value)}
                    placeholder="Fat%"
                    className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
                  />
                  <input
                    type="number"
                    value={mMuscle}
                    onChange={(e) => setMMuscle(e.target.value)}
                    placeholder="Muscle"
                    className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAddMetric}
                    disabled={metricSaving}
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-black hover:bg-primary-hover transition disabled:opacity-50"
                  >
                    {metricSaving ? "..." : "Save"}
                  </button>
                  <button
                    onClick={() => setShowMetricForm(false)}
                    className="text-xs text-muted hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!latestMetric && !showMetricForm && (
              <p className="px-4 py-4 text-xs text-muted text-center">No metrics recorded yet.</p>
            )}
          </section>

          {/* Recent workouts */}
          <section className="rounded-xl border border-white/10 bg-surface">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">
                Recent Workouts
                <span className="text-muted font-normal ml-1">({logs.length})</span>
              </h3>
            </div>
            {logs.length === 0 ? (
              <p className="px-4 py-4 text-xs text-muted text-center">No workouts logged yet.</p>
            ) : (
              <div className="max-h-60 overflow-y-auto divide-y divide-white/5">
                {logs.slice(0, 10).map((l) => (
                  <div key={l.id} className="px-4 py-2.5">
                    <p className="text-sm text-white">{l.routine?.name ?? "Unknown"}</p>
                    <p className="text-xs text-muted">
                      {new Date(l.performed_at.includes("T") ? l.performed_at : l.performed_at + "T00:00:00").toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                      {l.duration_minutes ? ` · ${l.duration_minutes} min` : ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* All Routines */}
        {allRoutines.length > 0 && (
          <section>
            <h2 className="mb-1 text-sm font-semibold text-white">My Training Program</h2>
            <p className="mb-3 text-xs text-muted">
              Podés iniciar cualquier rutina cualquier día, sin importar el día o la hora asignada.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {allRoutines.map((r) => (
                <div key={r.id} className="rounded-xl border border-white/10 bg-surface p-4">
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-white">{r.name}</p>
                      <p className="text-xs text-muted">
                        {r.scheduled_day !== undefined && r.scheduled_day !== null
                          ? ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][r.scheduled_day]
                          : "Unscheduled"}{" "}
                        &middot; {r.routine_exercises.length} exercises
                      </p>
                    </div>
                    {r.routine_exercises.length > 0 && (
                      <button
                        onClick={() => setActiveSession(r)}
                        className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-black hover:bg-primary-hover transition"
                      >
                        Empezar
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {r.routine_exercises.map((re) => (
                      <div key={re.id} className="flex items-center gap-2 text-xs">
                        <span className={`rounded-full px-1.5 py-0.5 ${getMuscleColor(re.exercise.muscle_group)}`}>
                          {re.exercise.muscle_group}
                        </span>
                        <span className="text-white">{re.exercise.name}</span>
                        <span className="text-muted ml-auto">
                          {re.sets}x{re.reps}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
